import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import AdyenCheckout from '@adyen/adyen-web';
import { NotificationFacade, PaymentMethodsFacade, ShoppingCartFacade, ShoppingCartState } from '@ridango/components';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AdyenResultStatus, AdyenSession, BaseComponent, CoreFacade, Language } from '@ridango/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'rwidget-adyen-integration',
    templateUrl: './adyen-integration.component.html'
})
export class AdyenIntegrationComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() redirectedPaymentResult;

    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;
    cart$: Observable<ShoppingCartState> = this.cartFacade.cart$;
    adyenSession$: Observable<AdyenSession> = this.paymentFacade.adyenSession$;

    selectedLanguageCode: string;
    isAdyenIntegrationReady = false;

    @Output() paymentResult = new EventEmitter();
    constructor(
        @Inject('ENV') public env,
        private coreFacade: CoreFacade,
        private router: Router,
        private cartFacade: ShoppingCartFacade,
        private paymentFacade: PaymentMethodsFacade,
        private notifications: NotificationFacade,
        private translate: TranslateService
    ) {
        super();
    }

    ngOnInit() {
        this.selectedLanguage$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((language) => (this.selectedLanguageCode = language.code));

        if (!this.redirectedPaymentResult.sessionId) {
            const paymentReturnUrl = this.env.PAYMENT_RETURN_URL + this.selectedLanguageCode + '/adyen';
            this.cart$.pipe(take(1)).subscribe((cart) => {
                this.paymentFacade.fetchAdyenSession(
                    cart.shopping_cart_id,
                    this.selectedLanguageCode,
                    paymentReturnUrl
                );
            });

            this.adyenSession$.pipe(takeUntil(this.componentDestroyed$)).subscribe(async (adyenSession) => {
                if (
                    adyenSession.client_key &&
                    adyenSession.session_data &&
                    adyenSession.session_id &&
                    !this.isAdyenIntegrationReady
                ) {
                    sessionStorage.setItem('adyen-client-key', adyenSession.client_key);
                    const session = {
                        id: adyenSession.session_id,
                        sessionData: adyenSession.session_data
                    };

                    const checkout = await this.createAdyenDropIn(session, adyenSession.client_key);

                    checkout
                        .create('dropin', {
                            onReady: () => (this.isAdyenIntegrationReady = true)
                        })
                        .mount('#dropin-container');
                }
            });
        } else {
            this.finalizeCheckout();
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    async finalizeCheckout() {
        try {
            const clientKey = sessionStorage.getItem('adyen-client-key');
            const checkout = await this.createAdyenDropIn({ id: this.redirectedPaymentResult.sessionId }, clientKey);
            checkout.submitDetails({ details: { redirectResult: this.redirectedPaymentResult.redirectResult } });
        } catch (error) {
            console.error(error);
            window.scrollTo(0, 0);
            this.notifications.error(this.translate.instant('UNKNOWN_ERROR'));
        }
    }

    async createAdyenDropIn(sessionPayload, clientKey: string) {
        const configuration = {
            environment: this.env.ENV === 'live' ? 'live' : 'test',
            clientKey: clientKey,
            analytics: {
                enabled: false
            },
            session: sessionPayload,
            onPaymentCompleted: (result) => {
                switch (result.resultCode) {
                    case AdyenResultStatus.AUTHORISED:
                        this.cartFacade.setCartStatus('Y');
                        this.notifications.success(this.translate.instant('ADYEN_PAYMENT_SUCCESS'), {
                            showAfterRouteChange: true
                        });
                        this.paymentResult.emit(result);
                        break;
                    case AdyenResultStatus.PENDING:
                    case AdyenResultStatus.RECEIVED:
                        this.cartFacade.setCartStatus('Y');
                        this.notifications.info(this.translate.instant('ADYEN_PAYMENT_PENDING_RECEIVED'), {
                            showAfterRouteChange: true
                        });
                        this.paymentResult.emit(result);
                        break;
                    case AdyenResultStatus.REFUSED:
                        this.notifications.error(this.translate.instant('ADYEN_PAYMENT_REFUSED'), {
                            showAfterRouteChange: true
                        });
                        this.router.navigate([this.selectedLanguageCode, 'purchase', 'cart']);
                        break;
                    case AdyenResultStatus.CANCELLED:
                        this.notifications.error(this.translate.instant('ADYEN_PAYMENT_CANCELLED'), {
                            showAfterRouteChange: true
                        });
                        this.router.navigate([this.selectedLanguageCode, 'purchase', 'cart']);
                        break;
                    default:
                        this.notifications.error(this.translate.instant('UNKNOWN_ERROR'));
                        this.router.navigate([this.selectedLanguageCode, 'purchase', 'cart']);
                        break;
                }
            },
            onError: (error, component) => {
                console.error(error.name, error.message, error.stack, component);

                window.scrollTo(0, 0);
                this.notifications.error(this.translate.instant('UNKNOWN_ERROR'));
            },

            paymentMethodsConfiguration: {
                card: {
                    hasHolderName: true,
                    holderNameRequired: true
                }
            }
        };

        return await AdyenCheckout(configuration);
    }
}
